// LocalStorage-based database for frontend-only deployment
// Data persists in browser localStorage
import {
  getBlogSchemaVersion,
  legacyMarkdownToHtml,
  normalizeBlogPost,
} from "./blogContent";

const BLOG_POSTS_KEY = "meet_enterprise_blog_posts";
const BLOG_POSTS_VERSION_KEY = "meet_enterprise_blog_posts_version";
const INQUIRIES_KEY = "meet_enterprise_inquiries";

// Initialize database
export const initDB = async () => {
  if (!localStorage.getItem(BLOG_POSTS_KEY)) {
    seedBlogPosts();
  } else {
    migrateBlogPosts();
  }
  return true;
};

// Seed initial blog posts
const seedBlogPosts = () => {
  const initialPosts = [
    {
      id: 1,
      title: "Understanding Acid Dyes: A Comprehensive Guide",
      slug: "understanding-acid-dyes",
      excerpt: "Learn everything about acid dyes, their applications, and why they're essential for textile dyeing.",
      content: legacyMarkdownToHtml(`# Understanding Acid Dyes: A Comprehensive Guide

Acid dyes are a class of dyes that are typically applied to protein fibers like wool, silk, and nylon. They are called acid dyes because they require an acidic environment to bond effectively with the fiber.

## What Are Acid Dyes?

Acid dyes are water-soluble anionic dyes that are applied to fibers from an acidic bath. These dyes are known for their bright, vibrant colors and excellent fastness properties.

## Applications

Acid dyes are primarily used for:
- Wool dyeing
- Silk dyeing
- Nylon dyeing
- Leather dyeing

## Key Characteristics

- Excellent color brightness
- Good wash fastness
- Light fastness
- Wide color range

## Best Practices

When using acid dyes, it's important to:
- Maintain proper pH levels
- Control temperature carefully
- Use appropriate mordants

## Conclusion

Acid dyes offer exceptional results for protein fibers when used correctly.`),
      contentFormat: "html",
      author: "Kavisha Shah",
      role: "Head of Business Development",
      category: "Textile Dyes",
      image: "https://images.unsplash.com/photo-1550684848-fac1c5b4e853?w=800",
      readTime: "5 min read",
      tags: ["Acid Dyes", "Textile", "Dyeing"],
      date: new Date().toISOString().split("T")[0],
    },
    {
      id: 2,
      title: "The Future of Reactive Dyes in Textile Industry",
      slug: "future-of-reactive-dyes",
      excerpt: "Explore how reactive dyes are revolutionizing the textile industry with their superior bonding properties.",
      content: legacyMarkdownToHtml(`# The Future of Reactive Dyes in Textile Industry

Reactive dyes represent a significant advancement in textile dyeing technology, offering superior color fastness and environmental benefits.

## What Makes Reactive Dyes Special?

Reactive dyes form a covalent bond with the fiber, resulting in excellent wash fastness and color retention.

## Environmental Benefits

Modern reactive dyes are becoming more environmentally friendly with:
- Reduced water consumption
- Lower chemical usage
- Biodegradable options

## Industry Trends

The textile industry is moving towards:
- Sustainable dyeing processes
- Digital printing technologies
- Smart textiles

## Conclusion

Reactive dyes continue to evolve, offering better performance and sustainability for the modern textile industry.`),
      contentFormat: "html",
      author: "Kavisha Shah",
      role: "Head of Business Development",
      category: "Textile Dyes",
      image: "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=800",
      readTime: "4 min read",
      tags: ["Reactive Dyes", "Sustainability", "Innovation"],
      date: new Date().toISOString().split("T")[0],
    },
  ];
  localStorage.setItem(
    BLOG_POSTS_KEY,
    JSON.stringify(initialPosts.map(normalizeBlogPost)),
  );
  localStorage.setItem(BLOG_POSTS_VERSION_KEY, String(getBlogSchemaVersion()));
};

const migrateBlogPosts = () => {
  try {
    const stored = localStorage.getItem(BLOG_POSTS_KEY);
    if (!stored) {
      return;
    }

    const posts = JSON.parse(stored);
    const currentVersion = Number(localStorage.getItem(BLOG_POSTS_VERSION_KEY) || 0);

    if (currentVersion >= getBlogSchemaVersion()) {
      return;
    }

    const migratedPosts = posts.map(normalizeBlogPost);
    localStorage.setItem(BLOG_POSTS_KEY, JSON.stringify(migratedPosts));
    localStorage.setItem(BLOG_POSTS_VERSION_KEY, String(getBlogSchemaVersion()));
  } catch (error) {
    console.error("Error migrating blog posts:", error);
  }
};

// Get all blog posts
export const getBlogPosts = async () => {
  try {
    const stored = localStorage.getItem(BLOG_POSTS_KEY);
    if (!stored) {
      seedBlogPosts();
      return JSON.parse(localStorage.getItem(BLOG_POSTS_KEY));
    }
    const posts = JSON.parse(stored).map(normalizeBlogPost);
    localStorage.setItem(BLOG_POSTS_KEY, JSON.stringify(posts));
    localStorage.setItem(BLOG_POSTS_VERSION_KEY, String(getBlogSchemaVersion()));
    return posts;
  } catch (error) {
    console.error("Error fetching blog posts:", error);
    return [];
  }
};

// Add blog post
export const addBlogPost = async (postData) => {
  try {
    const posts = await getBlogPosts();
    const newPost = {
      ...normalizeBlogPost(postData),
      id: posts.length > 0 ? Math.max(...posts.map((p) => p.id)) + 1 : 1,
      date: new Date().toISOString().split("T")[0],
    };
    posts.unshift(newPost);
    localStorage.setItem(BLOG_POSTS_KEY, JSON.stringify(posts));
    return newPost;
  } catch (error) {
    console.error("Error adding blog post:", error);
    return null;
  }
};

// Update blog post
export const updateBlogPost = async (id, postData) => {
  try {
    const posts = await getBlogPosts();
    const index = posts.findIndex((p) => p.id === id);
    if (index !== -1) {
      posts[index] = normalizeBlogPost({ ...posts[index], ...postData });
      localStorage.setItem(BLOG_POSTS_KEY, JSON.stringify(posts));
      return posts[index];
    }
    return null;
  } catch (error) {
    console.error("Error updating blog post:", error);
    return null;
  }
};

// Delete blog post
export const deleteBlogPost = async (id) => {
  try {
    const posts = await getBlogPosts();
    const filteredPosts = posts.filter((p) => p.id !== id);
    localStorage.setItem(BLOG_POSTS_KEY, JSON.stringify(filteredPosts));
    return filteredPosts;
  } catch (error) {
    console.error("Error deleting blog post:", error);
    return [];
  }
};

// Get blog post by slug
export const getBlogPostBySlug = async (slug) => {
  try {
    const posts = await getBlogPosts();
    return posts.find((p) => p.slug === slug);
  } catch (error) {
    console.error("Error fetching blog post by slug:", error);
    return null;
  }
};

// Get all inquiries
export const getInquiries = async () => {
  try {
    const stored = localStorage.getItem(INQUIRIES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error("Error fetching inquiries:", error);
    return [];
  }
};

// Add inquiry
export const saveInquiry = async (inquiryData) => {
  try {
    const inquiries = await getInquiries();
    const newInquiry = {
      ...inquiryData,
      id: inquiries.length > 0 ? Math.max(...inquiries.map((i) => i.id)) + 1 : 1,
      status: "new",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    inquiries.unshift(newInquiry);
    localStorage.setItem(INQUIRIES_KEY, JSON.stringify(inquiries));
    return newInquiry;
  } catch (error) {
    console.error("Error saving inquiry:", error);
    return null;
  }
};

// Update inquiry status
export const updateInquiryStatus = async (id, status) => {
  try {
    const inquiries = await getInquiries();
    const index = inquiries.findIndex((i) => i.id === id);
    if (index !== -1) {
      inquiries[index].status = status;
      inquiries[index].updatedAt = new Date().toISOString();
      localStorage.setItem(INQUIRIES_KEY, JSON.stringify(inquiries));
      return inquiries[index];
    }
    return null;
  } catch (error) {
    console.error("Error updating inquiry status:", error);
    return null;
  }
};

// Delete inquiry
export const deleteInquiry = async (id) => {
  try {
    const inquiries = await getInquiries();
    const filteredInquiries = inquiries.filter((i) => i.id !== id);
    localStorage.setItem(INQUIRIES_KEY, JSON.stringify(filteredInquiries));
    return filteredInquiries;
  } catch (error) {
    console.error("Error deleting inquiry:", error);
    return [];
  }
};

// Get inquiry by ID
export const getInquiryById = async (id) => {
  try {
    const inquiries = await getInquiries();
    return inquiries.find((i) => i.id === id);
  } catch (error) {
    console.error("Error fetching inquiry by ID:", error);
    return null;
  }
};

// Clear all data
export const clearAllData = async () => {
  try {
    localStorage.removeItem(BLOG_POSTS_KEY);
    localStorage.removeItem(INQUIRIES_KEY);
  } catch (error) {
    console.error("Error clearing data:", error);
  }
};
